/**
 * saas-auth `/api/account/**` — the signed-in member's own account (H §4.6). Cookies AND bearer;
 * `X-CSRF-Token` on writes (`client.ts`). Responses are saas-auth's `{ success, message?, data }`
 * envelope, unwrapped here. Devices, sessions, connected apps and re-verify are NOT available to
 * resellers (`403 NOT_SUPPORTED_FOR_RESELLER`) and are never called.
 */
import { request } from './client';

export interface AccountProfile {
  name: string;
  email: string;
  locale: string | null;
  emailVerified: boolean;
  /** False for a Google-only member — who sets a first password without `currentPassword`. */
  hasPassword: boolean;
  googleLinked: boolean;
}

export interface AccountMfa {
  /** `OPTIONAL` is the only policy under which the member may toggle MFA. */
  policy: string;
  mfaEnabled: boolean;
}

export interface PasswordChange {
  currentPassword?: string;
  newPassword: string;
  mfaCode: string;
}

function unwrap(body: unknown): Record<string, unknown> {
  let value = body;
  if (value && typeof value === 'object' && 'success' in value && 'data' in value) {
    value = (value as { data: unknown }).data;
  }
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function normalizeProfile(body: unknown): AccountProfile {
  const raw = unwrap(body);
  return {
    name: typeof raw.name === 'string' ? raw.name : '',
    email: typeof raw.email === 'string' ? raw.email : '',
    locale: typeof raw.locale === 'string' ? raw.locale : null,
    emailVerified: raw.emailVerified === true,
    hasPassword: raw.hasPassword === true,
    googleLinked: raw.googleLinked === true,
  };
}

function normalizeMfa(body: unknown): AccountMfa {
  const raw = unwrap(body);
  return {
    policy: typeof raw.policy === 'string' ? raw.policy : '',
    mfaEnabled: raw.mfaEnabled === true,
  };
}

export const accountApi = {
  async profile(): Promise<AccountProfile> {
    return normalizeProfile(await request<unknown>('GET', '/api/account/profile'));
  },

  async updateName(name: string): Promise<AccountProfile> {
    return normalizeProfile(
      await request<unknown>('PATCH', '/api/account/profile', { json: { name } }),
    );
  },

  /** Emails the member a 6-digit step-up code for `changePassword`. */
  async passwordChallenge(): Promise<void> {
    await request<unknown>('POST', '/api/account/password/challenge', { json: {} });
  },

  /** Success signs the member out EVERYWHERE — the caller must sign in again. */
  async changePassword(change: PasswordChange): Promise<void> {
    await request<unknown>('POST', '/api/account/password', { json: change });
  },

  async mfa(): Promise<AccountMfa> {
    return normalizeMfa(await request<unknown>('GET', '/api/account/mfa'));
  },

  /** Only when `policy === 'OPTIONAL'` (else `409 MFA_TOGGLE_NOT_ALLOWED`). The caller re-reads
   *  `mfa()` afterwards rather than trusting a response shape. */
  async toggleMfa(enabled: boolean): Promise<void> {
    await request<unknown>('POST', '/api/account/mfa/toggle', { json: { enabled } });
  },

  async updateLocale(locale: string): Promise<void> {
    await request<unknown>('PATCH', '/api/account/locale', { json: { locale } });
  },
};
